import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

const Question = ({ question, onAnswer, selectedAnswer, isCompleted, getAnswerButtonStyle, questionNumber }) => {
    const remarkPlugins = [[remarkMath]];
    const rehypePlugins = [[rehypeKatex, { strict: false }]];
    const [imageLoading, setImageLoading] = useState({});
    const [imageError, setImageError] = useState({});

    // Preprocess text to convert line breaks to markdown line breaks (two spaces + newline)
    // but preserve math expressions intact
    const preprocessText = (text) => {
        if (!text) return text;
        
        // Use regex to split text while preserving math expressions
        // Match display math ($$...$$) first, then inline math ($...$)
        const mathRegex = /(\$\$[\s\S]*?\$\$|\$[^\$\n]*?\$)/g;
        const parts = [];
        let lastIndex = 0;
        let match;
        
        // Find all math expressions
        while ((match = mathRegex.exec(text)) !== null) {
            // Add text before math
            if (match.index > lastIndex) {
                const textPart = text.substring(lastIndex, match.index);
                // Convert \n to markdown line breaks (two spaces + \n) in text parts
                parts.push(textPart.replace(/\n(?!\n)/g, '  \n'));
            }
            // Add math expression unchanged
            parts.push(match[0]);
            lastIndex = match.index + match[0].length;
        }
        
        // Add remaining text after last math expression
        if (lastIndex < text.length) {
            const textPart = text.substring(lastIndex);
            parts.push(textPart.replace(/\n(?!\n)/g, '  \n'));
        }
        
        return parts.join('');
    };

    // Determine the best layout based on question properties
    const getLayoutClass = () => {
        if (!question.imageUrl) return '';

        // If image URL contains specific keywords indicating diagram/graph
        const isDiagram = /diagram|graph|chart|plot|geometry|figure/i.test(question.imageUrl);
        
        // If question text is short (less than 100 characters)
        const isShortText = question.questionText.length < 100;
        
        // If question text has multiple paragraphs
        const hasMultipleParagraphs = question.questionText.split('\n\n').length > 1;

        if (isDiagram || (isShortText && !hasMultipleParagraphs)) {
            return 'image-above'; // Show image above for diagrams or short questions
        }

        if (hasMultipleParagraphs && !isShortText) {
            return 'image-side-by-side'; // Use side-by-side for longer, structured content
        }

        return ''; // Default to right-aligned float for normal cases
    };

    // Check if this question was unattempted (completed test but no selected answer)
    const isUnattempted = isCompleted && !selectedAnswer;

    const handleImageLoad = (imageId) => {
        setImageLoading(prev => ({ ...prev, [imageId]: false }));
        setImageError(prev => ({ ...prev, [imageId]: false }));
    };

    const handleImageError = (imageId, imageUrl) => {
        setImageLoading(prev => ({ ...prev, [imageId]: false }));
        setImageError(prev => ({ ...prev, [imageId]: true }));
        console.error('Failed to load image:', imageUrl);
    };

    const renderImage = (imageUrl, altText, imageId, customStyle = {}) => {
        const isLoading = imageLoading[imageId] !== false;
        const hasError = imageError[imageId];

        if (hasError) {
            return (
                <div style={{
                    ...customStyle,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f8f8',
                    borderRadius: '6px',
                    padding: '20px',
                    color: '#666',
                    fontSize: '14px'
                }}>
                    Image unavailable
                </div>
            );
        }

        return (
            <img
                src={imageUrl}
                alt={altText}
                style={{
                    ...customStyle,
                    opacity: isLoading ? 0.5 : 1,
                    transition: 'opacity 0.3s ease'
                }}
                onLoad={() => handleImageLoad(imageId)}
                onError={() => handleImageError(imageId, imageUrl)}
                onLoadStart={() => setImageLoading(prev => ({ ...prev, [imageId]: true }))}
            />
        );
    };

    return (
        <div className="question-container" style={{ position: 'relative' }}>
            {/* Unattempted Badge - Top Right Corner */}
            {isUnattempted && (
                <div style={{
                    position: 'absolute',
                    top: 'var(--spacing-sm)',
                    right: 'var(--spacing-sm)',
                    backgroundColor: 'var(--warning-light)',
                    color: 'var(--warning)',
                    padding: '4px 8px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: '600',
                    border: '1px solid var(--warning)',
                    zIndex: 10,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    Unattempted
                </div>
            )}

            {/* Question Content */}
            <div className="question-content" style={{
                paddingRight: isUnattempted ? '80px' : '0' // Add padding to prevent overlap
            }}>
                <div className={`question-text-with-image ${getLayoutClass()}`}>
                    <div className="question-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
                            {questionNumber && (
                                <span className="question-number">{questionNumber}</span>
                            )}
                        </div>
                        <div className="question-text-wrapper">
                            <div className="question-text">
                                <ReactMarkdown
                                    remarkPlugins={remarkPlugins}
                                    rehypePlugins={rehypePlugins}
                                >
                                    {preprocessText(question.questionText)}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </div>
                    {question.imageUrl && (
                        <div className="question-image-container">
                            {renderImage(question.imageUrl, "Question illustration", question.questionId)}
                        </div>
                    )}
                </div>
            </div>

            {/* Answer Options */}
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 'var(--spacing-sm)',
                ...(question.options.some(option => option.optionUrl) && {
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 'var(--spacing-md)'
                })
            }}>
                {question.options.map((option) => (
                    <button
                        key={option.optionId}
                        onClick={() => onAnswer(question.questionId, option)}
                        className="button secondary"
                        style={getAnswerButtonStyle(question, option)}
                        disabled={isCompleted}
                    >
                        {option.optionUrl && (
                            <div className="option-image-container">
                                {renderImage(option.optionUrl, "Option illustration", option.optionId, {
                                    maxWidth: '180px',
                                    maxHeight: '120px',
                                    marginBottom: '8px',
                                    objectFit: 'contain',
                                    borderRadius: '6px',
                                    background: '#f8f8f8',
                                    display: 'block'
                                })}
                            </div>
                        )}
                        <ReactMarkdown
                            remarkPlugins={remarkPlugins}
                            rehypePlugins={rehypePlugins}
                        >
                            {preprocessText(option.optionText)}
                        </ReactMarkdown>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default Question; 